import { Matrix2D, Point, createRotateMatrix, createScaleMatrix, createTranslateMatrix, multiplyMatrices } from '@imagise/shared';
import type { TCornerPoint } from '../../../object';
import type { InteractiveObject } from '../interactive-object';
import { renderCircleControl, renderSquareControl, ControlRenderingStyleOverride } from './control-rendering';

const DEFAULT_CURSOR: CSSStyleDeclaration['cursor'] = 'crosshair';

const isPointInPolygon = (point: Point, polygon: Point[]): boolean => {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;

    const intersects =
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;

    if (intersects) {
      inside = !inside;
    }
  }
  return inside;
};

export type ControlSet = Record<string, InteractiveControl>;

export type ControlRenderTarget = Pick<
  InteractiveObject,
  | 'cornerStyle'
  | 'cornerSize'
  | 'touchCornerSize'
  | 'cornerColor'
  | 'cornerStrokeColor'
  | 'transparentCorners'
  | 'getTotalAngle'
  | 'isControlVisible'
>;

export interface ControlOptions {
  x?: number;
  y?: number;
  offsetX?: number;
  offsetY?: number;
  sizeX?: number;
  sizeY?: number;
  touchSizeX?: number;
  touchSizeY?: number;
  angle?: number;
  cursorStyle?: CSSStyleDeclaration['cursor'];
  withConnection?: boolean;
  visible?: boolean;
}

/**
 * Object transformation control (handle)
 */
export class InteractiveControl {
  visible = true;
  actionName = 'scale';
  angle = 0;
  x = 0;
  y = 0;
  offsetX = 0;
  offsetY = 0;
  sizeX?: number;
  sizeY?: number;
  touchSizeX?: number;
  touchSizeY?: number;
  cursorStyle: CSSStyleDeclaration['cursor'] = DEFAULT_CURSOR;
  withConnection = false;

  constructor(options: ControlOptions = {}) {
    Object.assign(this, options);
  }

  /**
   * Checks whether the control should activate for the pointer position
   */
  shouldActivate(
    controlKey: string,
    object: ControlRenderTarget,
    pointer: Point,
    hitArea: TCornerPoint,
  ): boolean {
    if (!object.isControlVisible(controlKey)) {
      return false;
    }
    const polygon = [hitArea.tl, hitArea.tr, hitArea.br, hitArea.bl];
    return isPointInPolygon(pointer, polygon);
  }

  /**
   * Calculates the control position in viewport coordinates
   */
  positionHandler(
    dim: Point,
    finalMatrix: Matrix2D,
  ): Point {
    const position = new Point(this.x * dim.x + this.offsetX, this.y * dim.y + this.offsetY);
    return position.transform(finalMatrix);
  }

  /**
   * Calculates the control bounds for hit testing
   */
  calcCornerCoords(
    angle: number,
    cornerSize: number,
    centerX: number,
    centerY: number,
    useTouchArea: boolean,
    object: ControlRenderTarget,
  ): TCornerPoint {
    const sizeX = (useTouchArea ? this.touchSizeX : this.sizeX) || (useTouchArea ? object.touchCornerSize : cornerSize);
    const sizeY = (useTouchArea ? this.touchSizeY : this.sizeY) || (useTouchArea ? object.touchCornerSize : cornerSize);
    const translation = createTranslateMatrix(centerX, centerY);
    const rotation = createRotateMatrix(angle);
    const scale = createScaleMatrix(sizeX, sizeY);
    const matrix = multiplyMatrices(multiplyMatrices(translation, rotation), scale);

    return {
      tl: new Point(-0.5, -0.5).transform(matrix),
      tr: new Point(0.5, -0.5).transform(matrix),
      br: new Point(0.5, 0.5).transform(matrix),
      bl: new Point(-0.5, 0.5).transform(matrix),
    };
  }

  /**
   * Renders the control
   */
  render(
    ctx: CanvasRenderingContext2D,
    left: number,
    top: number,
    styleOverride: ControlRenderingStyleOverride | undefined,
    object: InteractiveObject,
  ): void {
    if ((styleOverride?.cornerStyle || object.cornerStyle) === 'circle') {
      renderCircleControl.call(this, ctx, left, top, styleOverride, object);
    } else {
      renderSquareControl.call(this, ctx, left, top, styleOverride, object);
    }
  }
}
